'use strict';

/**
 * Focus outline detection (FOD)
 * @module 'modules/focus-outline-detect'
 *
 * Remove the focus outline if using a pointing/touch device, and re-enables it if a key is pressed.
 */

class FocusOutlineDetect {
  /**
   * Construct a FOD instance with options.
   *
   * @param {object} [options] - Initialization options
   * @param {string} [options.helperClass='no-focus-outline'] - The className used for disabling focus outline
   * @param {HTMLElement} [options.rootElement=document.documentElement] - The element to which the helper class is added
   * @param {array} [options.disableEvents] - A list of event names that should trigger disabling the focus outline
   * @param {array} [options.enableEvents] - A list of event names that should trigger re-enabling the focus outline
   */
  constructor(options) {
    const defaults = {
      helperClass: 'no-focus-outline',
      rootElement: document.documentElement,
      disableEvents: ['mousemove', 'touchstart'],
      enableEvents: ['keydown']
    }
    this.settings = Object.assign({}, defaults, options);
    this._disabled = false;
  }

  /**
   * Initialize FOD.
   */
  init() {
    this.addCSS();
    this.bindEvents();
  }

  /**
   * Add CSS rules for removing focus outlines.
   */
  addCSS() {
    let cssEl = document.createElement('style');
    cssEl.innerHTML = this.disableCSS;
    document.head.appendChild(cssEl);
  }

  /**
   * Magic getter for CSS rules
   *
   * @returns {string} A CSS rule for disabling focus outlines
   */
  get disableCSS() {
    return `.${this.settings.helperClass} :focus { outline: 0; }`;
  }

  /**
   * Creates bound listener functions.
   * Iterates through the event names which disable and enable focus outlines.
   */
  bindEvents() {
    this.disableHandler = this.disableFocusOutline.bind(this);
    this.enableHandler = this.enableFocusOutline.bind(this);
    this.settings.disableEvents.forEach(this.bindDisableEvent, this);
    this.settings.enableEvents.forEach(this.bindEnableEvent, this);
  }

  /**
   * Binds an event which when triggered, disables focus outlines
   * @param {string} eventName - The name of the event
   */
  bindDisableEvent(eventName) {
    this.settings.rootElement.addEventListener(
      eventName,
      this.disableHandler,
      { passive: true }
    );
  }

  /**
   * Binds an event which when triggered, enbables focus outlines
   * @param {string} eventName - The name of the event
   */
  bindEnableEvent(eventName) {
    this.settings.rootElement.addEventListener(
      eventName,
      this.enableHandler,
      { passive: true }
    );
  }

  /**
   * Checks if focus outlines are disabled, and if not,
   * disables them by adding a class to the root element.
   */
  disableFocusOutline() {
    if(!this._disabled) {
      this.settings.rootElement.classList.add(
        this.settings.helperClass
      );
      this._disabled = true;
    }
  }

  /**
   * Checks if focus outlines are disabled, and if they are,
   * enables them by removing a class from the root element.
   *
   * @param {object} event - The current event object
   */
  enableFocusOutline(event) {
    if(this._disabled) {
      if(event.type === 'keydown') {
        if(event.key !== 'Tab') {
          return;
        }
      }
      this.settings.rootElement.classList.remove(
        this.settings.helperClass
      );
      this._disabled = false;
    }
  }
}

module.exports = FocusOutlineDetect;
